import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { OffersReportFiltersInput, OffersReportSortInput, EReportsSortDirection } from '__generated__/graphql';

interface ReportFiltersState {
  filters: OffersReportFiltersInput;
  sort: OffersReportSortInput[];
}

const initialState: ReportFiltersState = {
  filters: {},
  sort: [],
};

/**
 * Redux slice for managing offers report filters and sorting.
 * Handles mutually exclusive filter states (offerState, bookingState, cancellation).
 */
export const reportFiltersSlice = createSlice({
  name: 'reportFilters',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<OffersReportFiltersInput>) => {
      state.filters = action.payload;
    },
    /**
     * Updates a single filter field and clears mutually exclusive filters.
     * Only one of offerState, bookingState, or cancellation can be set at a time.
     */
    updateFilter: (state, action: PayloadAction<{ field: keyof OffersReportFiltersInput; value: any }>) => {
      const { field, value } = action.payload;
      const newFilters: OffersReportFiltersInput = { ...state.filters, [field]: value };

      // Clear mutually exclusive filters
      if (field === 'offerState') {
        newFilters.bookingState = undefined;
        newFilters.cancellation = undefined;
      }
      if (field === 'bookingState') {
        newFilters.offerState = undefined;
        newFilters.cancellation = undefined;
      }
      if (field === 'cancellation') {
        newFilters.bookingState = undefined;
        newFilters.offerState = undefined;
      }

      state.filters = newFilters;
    },
    setSort: (state, action: PayloadAction<OffersReportSortInput[]>) => {
      state.sort = action.payload;
    },
    /**
     * Updates sort order for a field.
     * If direction is provided, sets it directly. Otherwise toggles between asc/desc.
     * Only supports single-column sorting.
     */
    updateSort: (state, action: PayloadAction<{ field: keyof OffersReportSortInput; direction?: EReportsSortDirection }>) => {
      const { field, direction } = action.payload;

      if (direction) {
        state.sort = [{ [field]: direction }];
      } else if (state.sort.length > 0 && state.sort[0][field]) {
        if (state.sort[0][field] === 'asc') {
          state.sort = [{ [field]: 'desc' }];
        } else {
          state.sort = [{ [field]: 'asc' }];
        }
      } else {
        state.sort = [{ [field]: 'asc' }];
      }
    },
    resetReportFilters: (state) => {
      state.filters = {};
      state.sort = [];
    },
  },
});

export const { setFilters, updateFilter, setSort, updateSort, resetReportFilters } = reportFiltersSlice.actions;
export const reportFiltersReducer = reportFiltersSlice.reducer;

export default reportFiltersSlice;

